import React, { Fragment, useState, useEffect } from 'react';
import { CollapseCard } from 'components/UI';
import { useStyles } from './Actions.styled';
import { useDispatch, useSelector } from 'react-redux';
import { SubscriptionsActions } from 'state/modules/subscriptions';
import {
  ExchangeModal,
  EditPeriodsModal,
  ChangeVehicleModal,
  CancelSuccessModal,
  EditSubscriptionAddressModal,
} from 'components/Modals';
import i18n from 'i18n-js';
import { useConfirm } from 'hooks';
import moment from 'moment';

import {
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  ListItemSecondaryAction,
} from '@material-ui/core';

import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { useHistory } from 'react-router-dom';
import { ability } from 'config/ability';
import { buildUrl } from 'utils';

const Actions = ({ id, status, listStyles, source }) => {
  const classes = useStyles();

  const dispatch = useDispatch();

  const history = useHistory();

  const {
    loading,
    subscription,
    fetchedCancelSubscription,
    fetchingCancelSubscription,
    fetchedChangeVehicle,
    fetchingChangeVehicle,
    fetchedUpdateExchange,
    fetchingUpdateExchange,
  } = useSelector((state) => state.subscriptions || {});

  const { vehicle } = useSelector((state) => state.vehicles || {});

  const [openChangeVehicle, setOpenChangeVehicle] = useState(false);
  const [openEditAddress, setOpenEditAddress] = useState(false);
  const [openExchange, setOpenExchange] = useState(false);
  const [openPeriods, setOpenPeriods] = useState(false);
  const [action, setAction] = useState(null);
  const [openCancelSuccess, setOpenCancelSuccess] = useState(false);
  const { core } = useSelector((state) => state.config?.settings || {});
  const configSubscription = core?.subscription || {};

  const [disableAction, setDisableAction] = useState({});
  const [addressTypeAction, setAddressTypeAction] = useState('address');

  useEffect(() => {
    if (!loading) {
      setDisableAction({});
    }
  }, [loading, setDisableAction]);

  useEffect(() => {
    if (!fetchingCancelSubscription && fetchedCancelSubscription) {
      setOpenCancelSuccess(true);
    }
  }, [fetchingCancelSubscription, fetchedCancelSubscription]);

  useEffect(() => {
    if (!fetchingUpdateExchange && fetchedUpdateExchange) {
      dispatch(SubscriptionsActions.resetExchangeSubscription());
    }
  }, [fetchedUpdateExchange, fetchingUpdateExchange]); // eslint-disable-line

  const onCancel = () => {
    dispatch(SubscriptionsActions.fetchCancelSubscription(id));
  };

  const onCancelReturnProcess = () => {
    const payload = {
      exchange_event: 'END',
      action: 'CANCELEXCHANGE',
      action_time: moment(),
    };
    dispatch(SubscriptionsActions.fetchUpdateExchange(id, payload));
  };

  const onEndSubscription = () => {
    const payload = {
      exchange_event: 'END',
      action: 'SCHEDULEEXCHANGE',
      action_time: moment().add('days', 1),
    };
    dispatch(SubscriptionsActions.fetchUpdateExchange(id, payload));
  };

  const [toggleCancel, CancelConfirm] = useConfirm({
    title: i18n.t('subscriptions.cancel_subscription'),
    message: i18n.t('subscriptions.cancel_msg'),
    cancelText: i18n.t('common.no'),
    confirmText: i18n.t('common.yes'),
    onConfirm: onCancel,
  });

  const [toggleEndSubscription, EndSubscriptionConfirm] = useConfirm({
    title: i18n.t('subscriptions.end_subscription'),
    message: i18n.t('subscriptions.end_subscription_msg'),
    cancelText: i18n.t('common.no'),
    confirmText: i18n.t('common.yes'),
    onConfirm: onEndSubscription,
  });

  const [toggleCancelReturnProcess, CancelReturnProcessConfirm] = useConfirm({
    title: i18n.t('subscriptions.cancel_return_process'),
    message: i18n.t('subscriptions.cancel_return_process_msg'),
    cancelText: i18n.t('common.no'),
    confirmText: i18n.t('common.yes'),
    onConfirm: onCancelReturnProcess,
  });

  const getActions = (status) => {
    const actions = [
      {
        action: 'change_vehicle',
        enabled: ['STARTEXCHANGE', 'PENDING'].includes(status),
      },
      {
        action: 'edit_address',
        enabled: status && status !== 'ENDEXCHANGE' && status !== 'REJECTED',
      },
      {
        action: 'edit_delivery_pickup',
        enabled: status && status !== 'ENDEXCHANGE' && status !== 'REJECTED',
      },
      {
        action: 'edit_periods',
        enabled:
          configSubscription?.periods_max && configSubscription?.periods_min
            ? true
            : false && status !== 'REJECTED',
      },
      {
        action: 'schedule_delivery',
        enabled: ['STARTEXCHANGE'].includes(status),
      },
      {
        action: 'mark_delivered',
        enabled:
          ['STARTEXCHANGE'].includes(status) &&
          subscription?.exchanges?.START?.exchange_status === 'INPROGRESS',
      },
      {
        action: 'schedule_return',
        enabled: ['INPROGRESS', 'ENDEXCHANGE'].includes(status),
      },
      {
        action: 'mark_returned',
        enabled:
          ['ENDEXCHANGE'].includes(status) &&
          subscription?.exchanges?.END?.exchange_status === 'INPROGRESS',
      },
      {
        action: 'cancel_return_process',
        enabled: ['ENDEXCHANGE'].includes(status),
      },
      {
        action: 'end_subscription',
        enabled: ['INPROGRESS'].includes(status),
      },
      {
        action: 'refund',
        enabled:
          ability.can('cancel', 'receivable') &&
          status &&
          !['PENDING', 'REJECTED'].includes(status),
      },
      {
        action: 'cancel',
        enabled: ['PENDING', 'STARTEXCHANGE', 'REJECTED'].includes(status),
      },
    ];

    return actions.filter((val) => val.enabled);
  };

  const handleChangeVehicleModalOpen = () => {
    dispatch(SubscriptionsActions.resetChangeVehicle());
    setOpenChangeVehicle((prevState) => !prevState);
  };

  const handleEditAddressModalOpen = (type) => {
    setAddressTypeAction(type);
    setOpenEditAddress((prevState) => !prevState);
  };

  const handleExchangeModalOpen = () => {
    setOpenExchange((prevState) => !prevState);
  };

  const handlePeriodsModalOpen = () => {
    dispatch(SubscriptionsActions.resetUpdateSubscription());
    setOpenPeriods((prevState) => !prevState);
  };

  const handleCancelSuccessModalOpen = () => {
    if (openCancelSuccess) {
      dispatch(SubscriptionsActions.resteCancelSubscription());
    }
    setOpenCancelSuccess((prevState) => !prevState);
  };

  const changeVehicle = (values) => {
    dispatch(SubscriptionsActions.fetchChangeVehicle(id, { vehicle_id: values?.vehicle_id }));
  };

  const goToInvoicePage = () => {
    history.push(buildUrl(`/invoices?source_id=${id}&source_name=Subscription`));
  };

  const handleAction = (action) => {
    setDisableAction({
      [action]: true,
    });
    setAction(action);

    switch (action) {
      case 'change_vehicle':
        handleChangeVehicleModalOpen();
        break;

      case 'cancel':
        toggleCancel();
        break;

      case 'edit_address':
        handleEditAddressModalOpen('address');
        break;

      case 'edit_delivery_pickup':
        handleEditAddressModalOpen('vehicle');
        break;

      case 'edit_periods':
        handlePeriodsModalOpen();
        break;

      case 'schedule_return':
        handleExchangeModalOpen();
        break;

      case 'mark_returned':
        handleExchangeModalOpen();
        break;

      case 'mark_delivered':
        handleExchangeModalOpen();
        break;

      case 'schedule_delivery':
        handleExchangeModalOpen();
        break;

      case 'cancel_return_process':
        toggleCancelReturnProcess();
        break;

      case 'end_subscription':
        toggleEndSubscription();
        break;

      case 'refund':
        goToInvoicePage();
        break;

      default:
        break;
    }
  };

  return (
    <CollapseCard title="Actions" className={source ? classes[source] : ''} customClasses={classes}>
      <List>
        {getActions(status).map((item, index) => (
          <Fragment key={index}>
            <ListItem
              classes={{
                root: classes.listItem,
              }}
            >
              <ListItemText
                classes={listStyles}
                primary={i18n.t(`actions.${item?.action}`, { defaultValue: item?.action })}
              />
              <ListItemSecondaryAction>
                <IconButton
                  disabled={disableAction[item]}
                  onClick={() => handleAction(item?.action)}
                  edge="end"
                  classes={{
                    label: classes.label,
                    root: classes.iconRoot,
                  }}
                  aria-label="right"
                >
                  <ArrowForwardIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>

            {index !== getActions(status).length - 1 && (
              <Divider light={true} className={classes.divider} />
            )}
          </Fragment>
        ))}
      </List>
      {openChangeVehicle && (
        <ChangeVehicleModal
          open={true}
          onSubmit={changeVehicle}
          vehicle={subscription?.vehicle}
          vehicleId={subscription?.vehicle?.id}
          fetchedChangeVehicle={fetchedChangeVehicle}
          fetchingChangeVehicle={fetchingChangeVehicle}
          handleClick={handleChangeVehicleModalOpen}
          vehicle_group_id={vehicle?.vehicle_group?.id}
          privileges={{ action: 'update', subject: 'subscription' }}
        />
      )}
      {openEditAddress && (
        <EditSubscriptionAddressModal
          open={true}
          subscription={subscription || {}}
          addressType={addressTypeAction}
          handleClick={handleEditAddressModalOpen}
          privileges={{ action: 'update', subject: 'subscription' }}
        />
      )}
      {openCancelSuccess && (
        <CancelSuccessModal
          open={true}
          id={id}
          subscription={subscription || {}}
          handleClick={handleCancelSuccessModalOpen}
        />
      )}
      {openExchange && (
        <ExchangeModal
          open={true}
          action={action}
          subscription={subscription}
          handleClick={handleExchangeModalOpen}
        />
      )}
      {openPeriods && (
        <EditPeriodsModal
          open={true}
          subscription={subscription}
          handleClick={handlePeriodsModalOpen}
        />
      )}
      <CancelConfirm />
      <CancelReturnProcessConfirm />
      <EndSubscriptionConfirm />
    </CollapseCard>
  );
};

export default Actions;
